import Box from '@components/Box';
import ButtonWithTimer, { ButtonWithTimerType } from '@components/ButtonWithTimer/ButtonWithTimer';
import DigitInputContainer from '@containers/DigitInputContainer';
import FormErrorMessage from '@components/FormErrorMessage';
import parse from 'html-react-parser';
import Title from '@components/Title/Title.tsx';
import { Grid, Column, Row } from '@components/Grid';
import { linkVerify, linkSendCode, Seller, loginEmailVerify } from '@api/apiClient';
import { Text, TextSize, TextType } from '@components/Text';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation } from 'react-query';
import { useTranslation } from 'react-i18next';

export interface SendPinForm {
  pin: string;
}

interface ConfirmEmailProps {
  apiKey?: string;
  calledFrom?: string;
  clientId?: string;
  email: string;
  handlerSubmit?: () => void;
  name?: string;
  onSuccess: ((data?: Seller[]) => void) | (() => void);
  setTicketId?: (ticketId: string) => void;
  ticketId?: string;
}

const ConfirmEmail = (props: ConfirmEmailProps) => {
  const {
    apiKey,
    calledFrom,
    clientId,
    email,
    handlerSubmit,
    name,
    onSuccess,
    setTicketId,
    ticketId
  } = props;

  const { t } = useTranslation();
  const condition: boolean = calledFrom === 'profileData';

  const { control, watch } = useForm<SendPinForm>({
    defaultValues: {
      pin: "",
    },
  });

  const pinValue = watch("pin");

  const {
    error: errorLoginEmailVerify,
    isError: isErrorLoginEmailVerify,
    isLoading: isLoadingLoginEmailVerify,
    mutate: mutateLoginEmailVerify,
    reset: resetLoginEmailVerify
  } = useMutation({
    mutationFn: loginEmailVerify,
    onSuccess: () => onSuccess()
  });

  const verifyMutation = useMutation({
    mutationFn: linkVerify,
    onSuccess: ({ sellers }) => {
      onSuccess(sellers);
    },
  });

  const sendLinkMutation = useMutation({
    mutationFn: linkSendCode,
    onSuccess: ({ ticketId: newTicketId }) => {
      setTicketId && setTicketId(newTicketId);
    },
    onMutate: () => {
      verifyMutation.reset();
    },
  });

  const onSubmit = (pin: string) => {
    if (condition) {
      mutateLoginEmailVerify({
        code: pin,
        email: email
      })
    } else {
      verifyMutation.mutate({
        code: pin,
        ticketId,
        name: name || undefined,
        apiKey: apiKey || undefined,
        clientId: clientId || undefined,
      });
    }
  };

  const resendPin = () => {
    if (condition) {
      handlerSubmit!();
      resetLoginEmailVerify();
    } else {
      sendLinkMutation.mutate({ email, apiKey, clientId });
      verifyMutation.reset();
    }
  }

  useEffect(() => {
    if (condition) {
      resetLoginEmailVerify();
    } else {
      verifyMutation.reset();
    }
    if (pinValue?.trim().length === 4) {
      onSubmit(pinValue)
    }
  }, [pinValue])

  return (
    <Grid>
      <Row>
        <Column phone={12}>
          <Box pb={3}>
            <Title title={t("profileShops.addShopEnterCode")} />
          </Box>
        </Column>
      </Row>
      <Row>
        <Column phone={12}>
          <Box pb={10}>
            <Text
                size={TextSize.M}
                type={TextType.INLINE}
            >
              {parse(t('profileShops.addShopCodeSent', { email: email }))}
            </Text>
          </Box>
        </Column>
      </Row>

      <Row>
        <Column phone={12}>
          <Box pb={verifyMutation.isError ? 0 : 5}>
            <DigitInputContainer
                control={control}
                disabled={condition ? isLoadingLoginEmailVerify : verifyMutation.isLoading}
                hasError={condition ? isErrorLoginEmailVerify : verifyMutation.isError}
                length={4}
                name="pin"
                rules={{ required: t("profileShops.emailErrorText") }}
            />
          </Box>
        </Column>
      </Row>
      {(isErrorLoginEmailVerify || verifyMutation.isError) && (
          <Column phone={12}>
            <Box pb={5}>
              <FormErrorMessage error={condition ? errorLoginEmailVerify : verifyMutation.error} />
            </Box>
          </Column>
      )}
      <Row>
        <Column phone={12}>
          <Box>
            <ButtonWithTimer
              onClick={resendPin}
              pending={condition ? isLoadingLoginEmailVerify : sendLinkMutation.isLoading}
              label={t("profileShops.addShopResendCode")}
              type={ButtonWithTimerType.LINK}
              startTimerFromOutside
            />
          </Box>
        </Column>
      </Row>
    </Grid>
  )
}

export default ConfirmEmail;
